import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const ServiceHistoryModal = ({ machine, onClose }) => {
  const [serviceHistory, setServiceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (machine?.id) {
      fetchServiceHistory();
    }
  }, [machine?.id]);

  const fetchServiceHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('maintenance_schedule')
        .select('*')
        .eq('machine_id', machine.id)
        .order('scheduled_date', { ascending: false });

      if (fetchError) throw fetchError;

      // Normalize to match the component's expected shape
      const normalized = (data || []).map(item => ({
        id:             item.id,
        date:           item.scheduled_date,
        type:           item.maintenance_type,
        description:    item.notes || '—',
        technician:     item.technician_name || '—',
        duration:       item.duration_hours ? `${item.duration_hours} heure(s)` : '—',
        cost:           item.cost ?? 0,
        partsReplaced:  [], // not stored in DB schema
        status:         item.status,
        completedDate:  item.completed_date,
      }));

      setServiceHistory(normalized);
    } catch (err) {
      console.error('Error fetching service history:', err);
      setError(err?.message || 'Erreur lors du chargement de l\'historique');
      setServiceHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const getTypeConfig = (type) => {
    const configs = {
      preventive: { label: 'Préventive', className: 'bg-primary/10 text-primary', icon: 'Calendar' },
      corrective: { label: 'Corrective', className: 'bg-warning/10 text-warning', icon: 'Wrench' },
      emergency:  { label: 'Urgence',    className: 'bg-error/10 text-error',     icon: 'AlertTriangle' }
    };
    return configs?.[type] || configs?.preventive;
  };

  const getStatusConfig = (status) => {
    const configs = {
      scheduled:   { label: 'Planifiée',  className: 'bg-muted text-muted-foreground' },
      in_progress: { label: 'En cours',   className: 'bg-warning/10 text-warning'     },
      completed:   { label: 'Terminée',   className: 'bg-success/10 text-success'     },
      cancelled:   { label: 'Annulée',    className: 'bg-error/10 text-error'         },
    };
    return configs?.[status] || configs?.scheduled;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    })?.format(value ?? 0);
  };

  const totalCost    = serviceHistory?.reduce((sum, service) => sum + (service?.cost ?? 0), 0);
  const totalServices = serviceHistory?.length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{machine?.name || machine?.machine_name}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {machine?.model} - {machine?.serialNumber || machine?.machine_code}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-primary/5 rounded-lg">
              <div className="text-sm text-muted-foreground mb-1">Total Interventions</div>
              <div className="text-2xl font-bold text-foreground">{totalServices}</div>
            </div>
            <div className="p-4 bg-warning/5 rounded-lg">
              <div className="text-sm text-muted-foreground mb-1">Coût Total</div>
              <div className="text-2xl font-bold text-foreground">{formatCurrency(totalCost)}</div>
            </div>
            <div className="p-4 bg-success/5 rounded-lg">
              <div className="text-sm text-muted-foreground mb-1">Heures de fonctionnement</div>
              <div className="text-2xl font-bold text-foreground">
                {machine?.totalPrintHours ?? machine?.operating_hours ?? 0}h
              </div>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-foreground mb-4">Historique des Interventions</h3>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader" size={24} className="animate-spin text-primary mr-3" />
              <span className="text-muted-foreground">Chargement de l'historique...</span>
            </div>
          )}

          {error && !loading && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && serviceHistory.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Icon name="ClipboardList" size={32} className="text-muted-foreground" />
              </div>
              <h4 className="text-base font-semibold text-foreground mb-1">Aucune intervention</h4>
              <p className="text-sm text-muted-foreground text-center">
                Aucune maintenance n'a encore été enregistrée pour cette machine.
              </p>
            </div>
          )}

          {!loading && !error && serviceHistory.length > 0 && (
            <div className="space-y-4">
              {serviceHistory?.map(service => {
                const typeConfig   = getTypeConfig(service?.type);
                const statusConfig = getStatusConfig(service?.status);
                return (
                  <div key={service?.id} className="p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${typeConfig?.className}`}>
                            <Icon name={typeConfig?.icon} size={12} />
                            {typeConfig?.label}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusConfig?.className}`}>
                            {statusConfig?.label}
                          </span>
                          <span className="text-sm text-muted-foreground">{formatDate(service?.date)}</span>
                        </div>
                        <p className="text-sm text-foreground font-medium mb-1">{service?.description}</p>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-sm font-semibold text-foreground">
                          {service?.cost > 0 ? formatCurrency(service?.cost) : '—'}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Icon name="User" size={14} className="text-muted-foreground" />
                        <span className="text-foreground">{service?.technician}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Clock" size={14} className="text-muted-foreground" />
                        <span className="text-foreground">{service?.duration}</span>
                      </div>
                    </div>

                    {service?.completedDate && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-success">
                        <Icon name="CheckCircle2" size={12} />
                        <span>Terminée le {formatDate(service?.completedDate)}</span>
                      </div>
                    )}

                    {service?.partsReplaced?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="text-xs text-muted-foreground mb-2">Pièces remplacées :</div>
                        <div className="flex flex-wrap gap-2">
                          {service?.partsReplaced?.map((part, index) => (
                            <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs text-foreground">
                              <Icon name="Package" size={12} />
                              {part}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
          <Button iconName="Download">
            Exporter l'historique
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServiceHistoryModal;